import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { formatNumber } from '@lichtblick/localization';
import { Headline } from '@lichtblick/photon/atoms/headline/headline';
import { Text } from '@lichtblick/photon/atoms/text/text';
import { TextCTA } from '@lichtblick/photon/atoms/text-cta/text-cta';
import { InfoXs } from '@lichtblick/photon/icons/symbols/info-xs';
import { Stepper } from '@lichtblick/photon/molecules/stepper';
import { Modal } from '@lichtblick/photon/organisms/modal/modal';
import { useTracker } from '@lichtblick/tracker';
import classNames from 'classnames';
import { useCallback, useState } from 'react';
import styles from './calculator-usage-input.module.scss';
import { gasUsagePerSquareMeter, squareMeterLimit } from './constants';
const MODAL_ID = 'gas-usage-info-modal';
const calculateUsage = (livingSpace)=>livingSpace * gasUsagePerSquareMeter;
export const GasUsageInput = ({ children, onChange, usageRef })=>{
    const { trackCustomEvent } = useTracker();
    const [livingSpace, setLivingSpace] = useState(squareMeterLimit.default);
    // biome-ignore lint/correctness/useExhaustiveDependencies: TODO
    const handleStepperChange = useCallback((value)=>{
        trackCustomEvent({
            eventName: 'tariff_calculator_interaction'
        }, undefined, true);
        if (value) {
            const cleanedValue = Math.max(squareMeterLimit.min, Math.min(value, squareMeterLimit.max));
            setLivingSpace(cleanedValue);
            if (usageRef.current) {
                usageRef.current.value = calculateUsage(cleanedValue).toString();
                onChange(usageRef.current.value, livingSpace);
            }
        }
    }, [
        setLivingSpace
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx(Stepper, {
                defaultValue: livingSpace,
                label: "Wohnfläche in m²",
                max: squareMeterLimit.max,
                min: squareMeterLimit.min,
                name: "livingSpace",
                onChange: handleStepperChange,
                step: squareMeterLimit.step
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles['calculator-input-row'],
                children: [
                    /*#__PURE__*/ _jsxs(Text, {
                        size: "xs",
                        children: [
                            "Dein Verbrauch",
                            /*#__PURE__*/ _jsx("input", {
                                "aria-invalid": usageRef.current?.checkValidity() === false,
                                "aria-label": "Verbrauch in kWh/Jahr",
                                className: classNames(styles['calculator-usage-input'], usageRef.current?.checkValidity() === false && styles['calculator-usage-input-error']),
                                defaultValue: calculateUsage(squareMeterLimit.default),
                                name: "gas",
                                onChange: (event)=>onChange(event.target.value, livingSpace),
                                onClick: ()=>trackCustomEvent({
                                        eventName: 'tariff_calculator_interaction'
                                    }, undefined, true),
                                onInvalid: (e)=>e.preventDefault(),
                                ref: usageRef,
                                required: true
                            }),
                            "kWh/Jahr"
                        ]
                    }),
                    /*#__PURE__*/ _jsx(TextCTA, {
                        ariaLabel: "Informationen zur Berechnung des Gasverbrauchs zeigen",
                        as: "button",
                        icon: /*#__PURE__*/ _jsx(InfoXs, {}),
                        type: "button",
                        command: "show-modal",
                        commandFor: MODAL_ID
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Modal, {
                footerContent: /*#__PURE__*/ _jsx(TextCTA, {
                    as: "button",
                    type: "button",
                    command: "close",
                    commandFor: MODAL_ID,
                    "data-testid": "button-modal-close",
                    children: "Ok, verstanden"
                }),
                title: "So berechnet LichtBlick den Gasverbrauch",
                id: MODAL_ID,
                children: /*#__PURE__*/ _jsxs("div", {
                    className: styles['modal-content'],
                    children: [
                        /*#__PURE__*/ _jsx(Headline, {
                            renderAs: 'h2',
                            size: "s",
                            children: "So berechnet LichtBlick den Gasverbrauch"
                        }),
                        /*#__PURE__*/ _jsxs(Text, {
                            renderAs: 'p',
                            children: [
                                "Natürlich wisst nur ihr genau, was ihr verbraucht und könnt es deswegen auch direkt eintragen. Aus langer Erfahrung als Gasanbieter kann LichtBlick aber auch ziemlich gut schätzen: Im Schnitt werden pro Quadratmeter ",
                                formatNumber(gasUsagePerSquareMeter),
                                " kWh im Jahr verbraucht. Dabei ist natürlich miteingerechnet, dass im Sommer durchschnittlich deutlich weniger verbraucht wird als im Winter. Wenn zusätzlich mit Gas gekocht wird, kommen nochmal 1.000 kWh dazu. Diese müsstet ihr dann nur noch im Kalkulator hinzufügen, damit die Rechnung aufgeht. Doch weil auch wir bei der Berechnung nur mit Wasser kochen, könnt ihr den errechneten Wert jederzeit anpassen."
                            ]
                        })
                    ]
                })
            }),
            children
        ]
    });
};

//# sourceMappingURL=GasUsageInput.js.map